import React from 'react';
import { makeStyles, createStyles } from '@material-ui/styles';
import { FormSubmissionDetail, FormFieldConfig } from './form-responses-types';
import { formatSubmissionDate, sortFieldValuesByFormOrder, groupFieldValuesByInstance } from './form-responses-utils';

const useStyles = makeStyles(
  createStyles({
    printContainer: {
      padding: '32px',
      backgroundColor: '#fff',
      '& .print-org-header': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '12px',
        flexDirection: 'row',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '1px solid #ddd',
      },
      '& .print-org-name': {
        fontSize: '14px',
        fontWeight: 600,
        color: '#333',
      },
      '& .print-header': {
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #333',
      },
      '& .print-title': {
        fontSize: '24px',
        fontWeight: 700,
        color: '#333',
        marginBottom: '8px',
      },
      '& .print-subtitle': {
        fontSize: '14px',
        color: '#666',
      },
      '& .print-field': {
        padding: '12px 0',
        borderBottom: '1px solid #eee',
      },
      '& .print-field-label': {
        fontSize: '12px',
        fontWeight: 600,
        color: '#666',
        marginBottom: '4px',
      },
      '& .print-field-value': {
        fontSize: '14px',
        color: '#333',
      },
      '& .print-meta': {
        marginTop: '24px',
        paddingTop: '16px',
        borderTop: '1px solid #ddd',
        fontSize: '11px',
        color: '#999',
      },
    },
  })
);

interface PrintContentProps {
  printRef: React.RefObject<HTMLDivElement>;
  formTitle: string;
  selectedSubmission: FormSubmissionDetail | null;
  currentResponseNumber: number;
  totalSubmissions: number;
  formFields: FormFieldConfig[];
  orgLogo?: string | null;
  orgName?: string;
}

export const PrintContent: React.FC<PrintContentProps> = ({
  printRef,
  formTitle,
  selectedSubmission,
  currentResponseNumber,
  totalSubmissions,
  formFields,
  orgLogo,
  orgName,
}) => {
  const classes = useStyles();

  return (
    <div style={{ display: 'none' }}>
      <div ref={printRef} className={classes.printContainer}>
        {selectedSubmission && (
          <>
            {(orgLogo || orgName) && (
              <div className='print-org-header'>
                {orgLogo && (
                  <img src={orgLogo} alt={orgName || 'Organisation logo'} style={{ height: '40px', width: 'auto' }} />
                )}
                {orgLogo && orgName && (
                  <span style={{ display: 'inline-block', width: '1px', height: '28px', backgroundColor: '#ccc' }} />
                )}
                {orgName && <span className='print-org-name'>{orgName}</span>}
              </div>
            )}
            <div className='print-header'>
              <div className='print-title'>{formTitle}</div>
              <div className='print-subtitle'>
                Response {currentResponseNumber} of {totalSubmissions} |{' '}
                {formatSubmissionDate(selectedSubmission.submissionDate)}
              </div>
            </div>
            <div>
              {selectedSubmission.formFieldValues && selectedSubmission.formFieldValues.length > 0 ? (
                (() => {
                  const groupFieldIds = new Set<string>();
                  const groupMeta = new Map<string, { title: string; label: string }>();
                  formFields.forEach((f) => {
                    if (f.groupFormItemId) {
                      groupFieldIds.add(f.taskFieldId);
                      if (!groupMeta.has(f.groupFormItemId)) {
                        groupMeta.set(f.groupFormItemId, {
                          title: f.groupTitle || 'Group',
                          label: f.groupLabel || 'item',
                        });
                      }
                    }
                  });

                  const { ungrouped, grouped } = groupFieldValuesByInstance(
                    selectedSubmission.formFieldValues,
                    groupFieldIds
                  );

                  return (
                    <>
                      {sortFieldValuesByFormOrder(ungrouped, formFields).map((fv) => (
                        <div key={fv.formFieldValueId} className='print-field'>
                          <div className='print-field-label'>{fv.fieldName}</div>
                          <div className='print-field-value'>{fv.value || '-'}</div>
                        </div>
                      ))}
                      {Array.from(groupMeta.entries()).map(([groupId, meta]) => {
                        const instances = Array.from(grouped.values())
                          .map((g) => ({
                            instanceId: g.groupInstanceId,
                            values: g.values.filter((v) =>
                              formFields.some(
                                (ff) => ff.groupFormItemId === groupId && ff.taskFieldId === v.taskFieldId
                              )
                            ),
                          }))
                          .filter((inst) => inst.values.length > 0);

                        if (instances.length === 0) return null;
                        return (
                          <div key={groupId} style={{ marginTop: '16px' }}>
                            <div
                              style={{
                                fontWeight: 700,
                                fontSize: '14px',
                                borderBottom: '2px solid #333',
                                padding: '8px 0',
                              }}
                            >
                              {meta.title}
                            </div>
                            {instances.map((inst, idx) => (
                              <div key={inst.instanceId}>
                                {instances.length > 1 && (
                                  <div
                                    style={{ fontSize: '11px', fontWeight: 600, color: '#666', padding: '6px 0 2px' }}
                                  >
                                    {meta.label} {idx + 1}
                                  </div>
                                )}
                                {sortFieldValuesByFormOrder(inst.values, formFields).map((fv) => (
                                  <div key={fv.formFieldValueId} className='print-field'>
                                    <div className='print-field-label'>{fv.fieldName}</div>
                                    <div className='print-field-value'>{fv.value || '-'}</div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </>
                  );
                })()
              ) : (
                <div className='print-field-value'>No field values</div>
              )}
            </div>
            <div className='print-meta'>
              IP: {selectedSubmission.ipAddress || '-'} | User Agent: {selectedSubmission.userAgent || '-'} | Referrer:{' '}
              {selectedSubmission.referrer || '-'}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
